import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { withNamespaces } from 'react-i18next';

import { SectionTitle } from 'apps/group/styles';
import { TotalSeatsBar, BarItem, BarKey } from 'apps/payment/styles';
import {
  Div,
  FullWidthDivider,
  SettingsSection,
} from 'legacyComponents/styled/lib';

import Tooltip from 'components/Tooltip';
import { ConfirmModal, Spinner } from 'components';

export const SeatsBreakdown = withNamespaces()(
  observer(
    ({
      t,
      loading,
      seatsUsage,
      paymentSpace,
      isInitiative,
      onClickAddSeats,
    }) => {
      if (loading) {
        return (
          <SettingsSection>
            <SectionTitle level={2} size='medium'>
              {t('Seats Breakdown')}
            </SectionTitle>
            <FullWidthDivider />
            <Spinner margin='20px auto' />
          </SettingsSection>
        );
      }

      const [open, setOpen] = useState(false);

      const { subscription } = paymentSpace;
      const { seats, isCancelled } = subscription || {};
      const {
        usedSpaceSeats,
        usedProjectsSeats,
        usedInvitesSeats,
        unusedSeats,
        includedPlanSeats,
      } = seatsUsage || {};

      const removableSeats = unusedSeats - includedPlanSeats;

      const assignedSeats = usedSpaceSeats + usedProjectsSeats;

      const totalUsedSeats = unusedSeats + usedInvitesSeats + assignedSeats;

      if (!seatsUsage || !totalUsedSeats) return null;

      const totalBarItems = totalUsedSeats;

      const removeUnassignedSeats = () => {
        seats.RemoveSeats(removableSeats, () => {
          setOpen(false);
        });
      };

      const assignedBars = [];
      const availableBars = [];
      const invitedBars = [];
      for (let index = 0; index < assignedSeats; index++) {
        assignedBars.push(
          <BarItem
            key={`assigned-${index}`}
            data-testid={`billingSettingsPage-assignedSeats-bar-${index}`}
            active
            totalBarItems={totalBarItems}
            first={index === 0}
            last={
              assignedSeats === totalBarItems && index + 1 === totalBarItems
            }
          />,
        );
      }

      for (let index = 0; index < usedInvitesSeats; index++) {
        invitedBars.push(
          <BarItem
            key={`invited-${index}`}
            data-testid={`billingSettingsPage-invitedSeats-bar-${index}`}
            deepGray
            totalBarItems={totalBarItems}
            first={usedInvitesSeats === totalUsedSeats && index === 0}
            last={index + assignedSeats + 1 === totalBarItems}
          />,
        );
      }

      // eslint-disable-next-line
      for (let index = 0; index < unusedSeats; index++) {
        availableBars.push(
          <BarItem
            key={`available-${index}`}
            data-testid={`billingSettingsPage-availableSeats-bar-${index}`}
            active={false}
            totalBarItems={totalBarItems}
            first={unusedSeats === totalUsedSeats && index === 0}
            last={index + 1 === unusedSeats}
          />,
        );
      }

      const assignedToolTip = t(
        'Assigned to Admins or Editors in your Projects and Files.',
      );
      const invitedToolTip = t(
        'Admins or Editors that are invited but not yet confirmed.',
      );
      const availableToolTip = t(
        'Available to assign to new Admins or Editors in your Projects and Files.',
      );

      return (
        <Div width='100%'>
          <div className='flex justify-between'>
            <SectionTitle style={{ marginBottom: 0 }} level={2} size='medium'>
              {t('Seats Breakdown')}
            </SectionTitle>
            {!isCancelled && (
              <div className='flex justify-between mt-1'>
                {!!removableSeats && (
                  <div
                    onClick={() => {
                      setOpen(true);
                    }}
                    className='flex mr-3 hover:text-red-500 cursor-pointer'
                    data-testid='billingSettingsPage-removeUnassignedSeats-button'
                  >
                    {t('Remove Unassigned Seats')}
                  </div>
                )}

                {!!subscription && (
                  <div
                    className='flex text-blue-500 cursor-pointer'
                    data-testid='billingSettingsPage-addSeats-button'
                    onClick={() => {
                      onClickAddSeats();
                    }}
                  >
                    {t('+Add Seats')}
                  </div>
                )}
              </div>
            )}
          </div>
          <FullWidthDivider margin='0px 0px 20px 0px' />
          <div className='flex justify-between'>
            <b
              className='text-black'
              data-testid='billingSettingsPage-totalUsedSeats-text'
            >
              {t(`${totalUsedSeats} Seats:`)}
            </b>
            <div className='text-black flex justify-around'>
              {assignedSeats > 0 && (
                <div
                  className='flex items-center mr-1 text-black'
                  data-testid='billingSettingsPage-totalAssignedSeats-text'
                >
                  <BarKey active /> {assignedSeats} Assigned
                </div>
              )}
              {usedInvitesSeats > 0 && (
                <div
                  className='flex items-center text-black'
                  data-testid='billingSettingsPage-totalInvitedSeats-text'
                >
                  <BarKey deepGray /> {usedInvitesSeats} Invited
                </div>
              )}
              {unusedSeats > 0 && (
                <div
                  className='flex items-center text-black'
                  data-testid='billingSettingsPage-unusedSeats-text'
                >
                  <BarKey /> {unusedSeats} Available
                </div>
              )}
            </div>
          </div>
          <TotalSeatsBar>
            <Tooltip delayShow={500} />
            <span data-tip={assignedToolTip}>{assignedBars}</span>
            <span
              data-tip={invitedToolTip}
              data-place='bottom'
              data-effect='solid'
              data-delay-show='500'
            >
              {invitedBars}
            </span>
            <span
              data-tip={availableToolTip}
              data-place='bottom'
              data-effect='solid'
              data-delay-show='500'
            >
              {availableBars}
            </span>
          </TotalSeatsBar>
          <ConfirmModal
            open={open}
            titleStyle={{
              minWidth: 720,
            }}
            title='Are you sure about removing unassigned seats?'
            loading={seats?.loading}
            onConfirm={removeUnassignedSeats}
            onClose={() => {
              setOpen(false);
            }}
          />
          {isInitiative && totalUsedSeats > 1 && (
            <div>
              {usedSpaceSeats > 0 && (
                <span className='inline-block mr-2 text-black text-sm'>
                  {usedSpaceSeats} member{usedSpaceSeats > 1 && 's'}
                  {usedProjectsSeats > 0 && ' ,'}
                </span>
              )}
              {usedProjectsSeats > 0 && (
                <span className='text-black text-sm'>
                  {usedProjectsSeats} outside collaborator
                  {usedProjectsSeats > 1 && 's'}
                </span>
              )}
            </div>
          )}
        </Div>
      );
    },
  ),
);

SeatsBreakdown.propTypes = {
  subscription: PropTypes.object,
  isInitiative: PropTypes.bool,
};
